import { observer } from "mobx-react-lite"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { forwardRef, useMemo, useRef, useCallback } from "react"
import styles from "./styles.module.scss"
import { Control } from "react-hook-form"
import FormTextInput from "../FormTextInput"
import ArrowBackOutline from "~icons/eva/arrow-back-outline"
import ArrowForwardOutline from "~icons/eva/arrow-forward-outline"
import CalendarIcon from "~icons/eva/calendar-outline"
import {
  datesFormats,
  formatDateToString,
  formatLocalDate,
  dateStringToDate,
} from "@cruisen/react-shared"
import { makeAutoObservable } from "mobx"
import {
  isValidDate,
  createMonthDate,
  getMonthBoundary,
  isSameMonth,
  getDayClassName,
} from "./utils"
import { colors } from "@theme/colors"

const CustomInput = forwardRef(
  (
    {
      onClick,
      ...props
    }: {
      onClick?: () => void
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      control?: Control<any, any>
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    return (
      <div className={styles.customInput} ref={ref}>
        <FormTextInput
          name="startDate"
          control={props.control}
          label="Start date"
          className={styles.dateInput}
          parentBackgroundColor={colors.backgroundQuaternary}
          lightOutline={true}
          onClick={onClick}
          readOnly
          iconRight={<CalendarIcon className={styles.calendarIcon} />}
          testID="cruise-date-picker-start-date-input"
        />
        <span>-</span>
        <FormTextInput
          name="endDate"
          control={props.control}
          label="End date"
          className={styles.dateInput}
          parentBackgroundColor={colors.backgroundQuaternary}
          lightOutline={true}
          onClick={onClick}
          readOnly
          iconRight={<CalendarIcon className={styles.calendarIcon} />}
          testID="cruise-date-picker-end-date-input"
        />
      </div>
    )
  },
)

class CruiseDatePickerStore {
  selectedStartDate: Date | null
  selectedEndDate: Date | null

  constructor(initialStartDate: string | null, initialEndDate: string | null) {
    this.selectedStartDate = initialStartDate ? dateStringToDate(initialStartDate) : null
    this.selectedEndDate = initialEndDate ? dateStringToDate(initialEndDate) : null

    makeAutoObservable(this)
  }

  setSelectedStartDate(value: Date | null) {
    this.selectedStartDate = value
  }

  setSelectedEndDate(value: Date | null) {
    this.selectedEndDate = value
  }
}

export const CruiseDatePicker = observer(function CruiseDatePicker({
  availableMonths,
  initialStartDate,
  initialEndDate,
  onSave,
  control,
  setIsDatePickerOpen,
}: {
  availableMonths: number[]
  initialStartDate: string | null
  initialEndDate: string | null
  onSave: (dates: [string | null, string | null]) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any, any>
  setIsDatePickerOpen: (value: boolean) => void
}) {
  const datePickerRef = useRef<DatePicker>(null)
  const store = useMemo(
    () => new CruiseDatePickerStore(initialStartDate, initialEndDate),
    [initialStartDate, initialEndDate],
  )

  const months = useMemo(() => {
    return availableMonths.map(createMonthDate).filter((date): date is Date => date !== null)
  }, [availableMonths])

  const minDate = useMemo(
    () => (months.length > 0 ? getMonthBoundary(months[0]) : undefined),
    [months],
  )

  const maxDate = useMemo(
    () => (months.length > 0 ? getMonthBoundary(months[months.length - 1], true) : undefined),
    [months],
  )

  const handleDateChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates.map((date) => (isValidDate(date) ? date : null))
      store.setSelectedStartDate(start)
      store.setSelectedEndDate(end)
    },
    [store],
  )

  const handleSave = useCallback(() => {
    onSave([
      formatDateToString(store.selectedStartDate, "yyyy-MM-dd"),
      formatDateToString(store.selectedEndDate, "yyyy-MM-dd"),
    ])
    setIsDatePickerOpen(false)
    datePickerRef.current?.setOpen(false)
  }, [onSave, store.selectedStartDate, store.selectedEndDate, setIsDatePickerOpen])

  const isDateInAvailableMonths = useCallback(
    (date: Date) => {
      return months.some((m) => isSameMonth(m, date))
    },
    [months],
  )

  const onClickOutside = (e: MouseEvent) => {
    const filteringModal = document.querySelector('[data-testid="filtering-popover"]')

    // Clicking outside of the date picker but inside the filtering modal
    if (filteringModal?.contains(e.target as Node)) {
      setIsDatePickerOpen(false)
    }
  }

  return (
    <DatePicker
      ref={datePickerRef}
      selected={store.selectedStartDate}
      startDate={isValidDate(store.selectedStartDate) ? store.selectedStartDate : undefined}
      endDate={isValidDate(store.selectedEndDate) ? store.selectedEndDate : undefined}
      selectsRange
      disabledKeyboardNavigation
      onChange={handleDateChange}
      shouldCloseOnSelect={false}
      onInputClick={() => setIsDatePickerOpen(true)}
      onClickOutside={onClickOutside}
      minDate={minDate}
      maxDate={maxDate}
      formatWeekDay={(date) => date.toString().slice(0, 3)}
      filterDate={isDateInAvailableMonths}
      calendarClassName={styles.calendar}
      popperClassName={styles.popper}
      dayClassName={(day) =>
        getDayClassName(day, store.selectedStartDate, store.selectedEndDate, styles)
      }
      weekDayClassName={() => styles.weekDay}
      customInput={<CustomInput control={control} />}
      renderCustomHeader={({
        monthDate,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <div className={styles.header}>
          <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
            <ArrowBackOutline className={styles.icon} />
          </button>
          <span>{formatLocalDate(monthDate.toISOString(), datesFormats.monthWithYear)}</span>
          <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
            <ArrowForwardOutline className={styles.icon} />
          </button>
        </div>
      )}
    >
      <button
        className={styles.saveButton}
        onClick={handleSave}
        disabled={
          store.selectedStartDate?.getTime() ===
            (initialStartDate ? dateStringToDate(initialStartDate).getTime() : null) &&
          store.selectedEndDate?.getTime() ===
            (initialEndDate ? dateStringToDate(initialEndDate).getTime() : null)
        }
        data-testid="cruise-date-picker-save-button"
      >
        Save
      </button>
    </DatePicker>
  )
})
